import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  useToast,
  Alert,
  AlertIcon,
  SimpleGrid,
  Flex,
  Icon,
  Tooltip,
  List,
  ListItem,
  ListIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Image
} from '@chakra-ui/react';
import {
  FiUsers,
  FiTrendingUp,
  FiBarChart2,
  FiDownload,
  FiRefreshCw,
  FiTarget,
  FiClock,
  FiActivity,
  FiAward,
  FiTrendingDown,
  FiPercent,
  FiStar,
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
  FiImage,
  FiCheck,
  FiEye
} from 'react-icons/fi';
import { useDataStore } from '../../contexts/DataStoreContext';
import { LoadingSpinner } from '../Common/LoadingSpinner';
import {
  generateEventSummaryCard,
  generateFanFavoritesCard,
  generateVoteTimelineHeatmap,
  generateFightHighlightCard,
  generateKeyInsightCard,
  downloadCanvas
} from '../../utils/graphicsGenerator';

export const EventAnalytics = ({ event }) => {
  const dataStore = useDataStore();
  const [analytics, setAnalytics] = useState(null);
  const [realTimeStats, setRealTimeStats] = useState(null);
  const [keyLearnings, setKeyLearnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [showAllFighters, setShowAllFighters] = useState(false);
  const [generatingGraphic, setGeneratingGraphic] = useState(false);
  const [previewGraphic, setPreviewGraphic] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Helper function to convert 24-hour time to 12-hour format
  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  // Helper function to check if all fights in the event are completed
  const areAllFightsCompleted = useCallback(() => {
    const eventFights = Array.from(dataStore.fights.values()).filter(fight => fight.eventId === event.id);
    return eventFights.length > 0 && eventFights.every(fight => fight.status === 'completed');
  }, [dataStore.fights, event.id]);

  // Calculate top voters - users ranked by most correct votes in event
  const getTopVoters = useCallback(() => {
    const eventVotes = Array.from(dataStore.votes.values()).filter(vote => vote.eventId === event.id);
    const eventFights = Array.from(dataStore.fights.values()).filter(fight => fight.eventId === event.id);
    const completedFights = eventFights.filter(f => f.status === 'completed' && f.winnerId);

    // Track both correct and total votes for each user in this event
    const userStats = {};

    eventVotes.forEach(vote => {
      if (vote.userId) {
        if (!userStats[vote.userId]) {
          userStats[vote.userId] = { correct: 0, total: 0 };
        }

        const fight = eventFights.find(f => f.id === vote.fightId);

        // Only count votes for completed fights with winners
        if (fight && fight.status === 'completed' && fight.winnerId) {
          userStats[vote.userId].total += 1;

          // Check if user voted for the winner
          if (vote.fighterId === fight.winnerId) {
            userStats[vote.userId].correct += 1;
          }
        }
      }
    });

    // Convert to array and add user details
    const rankedUsers = Object.entries(userStats)
      .map(([userId, stats]) => {
        const userProfile = Array.from(dataStore.users.values()).find(u => u.id === userId);
        const accuracy = stats.total > 0 ? (stats.correct / stats.total) * 100 : 0;

        return {
          userId,
          correctVotes: stats.correct,
          totalVotes: stats.total,
          accuracy,
          email: userProfile?.email || 'Unknown',
          displayName: userProfile?.displayName || 'Unknown User'
        };
      })
      .sort((a, b) => {
        // Sort by correct votes first
        if (a.correctVotes !== b.correctVotes) {
          return b.correctVotes - a.correctVotes;
        }
        // Then by accuracy
        if (a.accuracy !== b.accuracy) {
          return b.accuracy - a.accuracy;
        }
        // Then by total votes
        if (a.totalVotes !== b.totalVotes) {
          return b.totalVotes - a.totalVotes;
        }
        // Finally alphabetically by name (matches leaderboard logic)
        return a.displayName.localeCompare(b.displayName);
      });

    if (rankedUsers.length === 0) return [];

    // Determine ranks with tie handling
    let currentRank = 1;
    const usersWithRanks = [];

    rankedUsers.forEach((user, index) => {
      // If not the first user and score is different from previous, update rank
      if (index > 0 && user.correctVotes < rankedUsers[index - 1].correctVotes) {
        currentRank = index + 1;
      }

      // Only include users in positions 1-5 (considering ties)
      // Stop after we've included position 5, unless there are ties at position 5
      if (currentRank <= 3) {
        // Always include top 3 ranks (with all ties)
        usersWithRanks.push({ ...user, rank: currentRank });
      } else if (usersWithRanks.length < 5) {
        // Include 4th and 5th if we haven't reached 5 users yet
        usersWithRanks.push({ ...user, rank: currentRank });
      }
    });

    return usersWithRanks.slice(0, 5); // Return max 5 users
  }, [dataStore.votes, dataStore.users, dataStore.fights, event.id]);

  // Calculate fan favorites - fighters ranked by total votes received
  const getFanFavorites = useCallback((showAll = false) => {
    const eventVotes = Array.from(dataStore.votes.values()).filter(vote => vote.eventId === event.id);
    const fighterVotes = {};

    // Count votes for each fighter
    eventVotes.forEach(vote => {
      if (vote.fighterId) {
        fighterVotes[vote.fighterId] = (fighterVotes[vote.fighterId] || 0) + 1;
      }
    });

    // Convert to array and add fighter details
    const rankedFighters = Object.entries(fighterVotes)
      .map(([fighterId, votes]) => {
        const fighter = dataStore.getFighter(fighterId);
        const eventFights = Array.from(dataStore.fights.values()).filter(fight =>
          fight.eventId === event.id && (fight.fighter1Id === fighterId || fight.fighter2Id === fighterId)
        );

        return {
          fighterId,
          fighter,
          votes,
          fightsInEvent: eventFights.length
        };
      })
      .filter(item => item.fighter) // Only include fighters we have data for
      .sort((a, b) => b.votes - a.votes); // Sort by total votes descending

    // Return all fighters or just top 10 based on parameter
    return showAll ? rankedFighters : rankedFighters.slice(0, 10);
  }, [dataStore.votes, dataStore.fights, dataStore.getFighter, event.id]);

  const loadAnalytics = useCallback(() => {
    try {
      setLoading(true);
      setError('');

      // Wait for DataStore to be loaded before accessing data
      if (dataStore.isLoading) {
        return;
      }

      const data = dataStore.getEventAnalytics(event.id);
      setAnalytics(data);
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  }, [dataStore, event.id]);

  const loadRealTimeStats = useCallback(() => {
    try {
      if (dataStore.isLoading) {
        return;
      }

      // Calculate truly live statistics based on recent activity
      const now = new Date();
      const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
      const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

      // Get all votes for this event
      const eventVotes = Array.from(dataStore.votes.values()).filter(vote => vote.eventId === event.id);

      // Recent votes (last 5 minutes)
      const recentVotes = eventVotes.filter(vote =>
        vote.createdAt && vote.createdAt >= fiveMinutesAgo
      );

      // Votes in last hour
      const hourlyVotes = eventVotes.filter(vote =>
        vote.createdAt && vote.createdAt >= oneHourAgo
      );

      // Active users (users who voted in last hour)
      const activeUserIds = new Set(hourlyVotes.map(vote => vote.userId));
      const activeUsers = activeUserIds.size;

      // Recent users (users who voted in last 5 minutes)
      const recentUserIds = new Set(recentVotes.map(vote => vote.userId));
      const recentUsers = recentUserIds.size;

      // Calculate voting rate (votes per minute in last hour)
      const votingRate = hourlyVotes.length / 60; // votes per minute

      // Total votes for the event
      const totalVotes = eventVotes.length;

      // Average votes per active user
      const avgVotesPerActiveUser = activeUsers > 0 ? (hourlyVotes.length / activeUsers) : 0;

      const liveStats = {
        totalVotes,
        activeUsers,
        recentUsers,
        recentVotes: recentVotes.length,
        hourlyVotes: hourlyVotes.length,
        votingRate: votingRate.toFixed(1),
        avgVotesPerActiveUser: avgVotesPerActiveUser.toFixed(1),
        generatedAt: now,
        // For backward compatibility, include these fields
        voteStats: { totalVotes },
        userEngagement: { totalUsers: activeUsers }
      };

      setRealTimeStats(liveStats);
    } catch (err) {
      console.error('Real-time stats error:', err);
    }
  }, [dataStore, event.id]);

  const generateKeyLearnings = useCallback(() => {
    try {
      if (dataStore.isLoading || !analytics) {
        return;
      }

      const data = dataStore.getEventAnalytics(event.id);
      const learnings = [];

      // Analyze prediction accuracy patterns
      const completedFights = data.fightStats.filter(f => f.status === 'completed');
      if (completedFights.length > 0) {
        const avgAccuracy = completedFights.reduce((sum, f) => sum + (f.predictionAccuracy || 0), 0) / completedFights.length;

        if (avgAccuracy > 70) {
          learnings.push({
            type: 'success',
            title: 'High Prediction Accuracy',
            insight: `Users achieved ${avgAccuracy.toFixed(1)}% average prediction accuracy, indicating strong fight knowledge and analysis.`,
            icon: FiCheckCircle
          });
        } else if (avgAccuracy < 40) {
          learnings.push({
            type: 'warning',
            title: 'Unpredictable Results',
            insight: `Low prediction accuracy (${avgAccuracy.toFixed(1)}%) suggests many upset victories or highly competitive matchups.`,
            icon: FiAlertCircle
          });
        }
      }

      // Analyze user engagement patterns
      const engagementRate = data.userEngagement.totalUsers > 0 ?
        (data.voteStats.totalVotes / data.userEngagement.totalUsers) : 0;

      if (engagementRate > 8) {
        learnings.push({
          type: 'success',
          title: 'High User Engagement',
          insight: `Users cast an average of ${engagementRate.toFixed(1)} votes each, showing strong event engagement and interest.`,
          icon: FiTrendingUp
        });
      } else if (engagementRate < 3) {
        learnings.push({
          type: 'info',
          title: 'Selective Voting Behavior',
          insight: `Users averaged ${engagementRate.toFixed(1)} votes each, suggesting selective engagement with specific fights.`,
          icon: FiInfo
        });
      }

      // Analyze fight popularity distribution
      const fightVoteCounts = data.fightStats.map(f => f.totalVotes);
      if (fightVoteCounts.length > 1) {
        const maxVotes = Math.max(...fightVoteCounts);
        const minVotes = Math.min(...fightVoteCounts);
        const variance = maxVotes - minVotes;

        if (variance > maxVotes * 0.5) {
          const mostPopularFight = data.fightStats.find(f => f.totalVotes === maxVotes);
          learnings.push({
            type: 'info',
            title: 'Uneven Fight Interest',
            insight: `"${mostPopularFight?.fightName}" drew ${maxVotes} votes while others had significantly fewer, indicating varying fan interest levels.`,
            icon: FiBarChart2
          });
        }
      }

      // Analyze voting timeline patterns
      if (data.timelineData.length > 0) {
        const peakHour = data.timelineData.reduce((max, curr) => curr.votes > max.votes ? curr : max);
        const totalTimelineVotes = data.timelineData.reduce((sum, slot) => sum + slot.votes, 0);
        const peakPercentage = (peakHour.votes / totalTimelineVotes) * 100;

        if (peakPercentage > 40) {
          learnings.push({
            type: 'warning',
            title: 'Concentrated Voting Window',
            insight: `${peakPercentage.toFixed(1)}% of votes occurred at ${formatHour(peakHour.hour)}, suggesting a narrow engagement window.`,
            icon: FiClock
          });
        }
      }

      // Performance vs expectations
      if (data.fightStats.length > 0) {
        const upsetCount = completedFights.filter(f => {
          const voteEntries = Object.entries(f.voteDistribution);
          if (voteEntries.length < 2) return false;

          const [mostVotedId, mostVotes] = voteEntries.reduce((max, curr) =>
            curr[1] > max[1] ? curr : max
          );

          // Check if the most voted fighter lost (if we have winner data)
          return f.winnerId && f.winnerId !== mostVotedId && mostVotes > f.totalVotes * 0.6;
        }).length;

        if (upsetCount > completedFights.length * 0.3) {
          learnings.push({
            type: 'warning',
            title: 'High Upset Rate',
            insight: `${upsetCount} of ${completedFights.length} completed fights resulted in upsets, defying crowd predictions.`,
            icon: FiTrendingDown
          });
        }
      }

      setKeyLearnings(learnings);
    } catch (err) {
      console.error('Key learnings generation error:', err);
    }
  }, [dataStore, analytics, event.id]);

  useEffect(() => {
    if (event?.id && !dataStore.isLoading) {
      loadAnalytics();
      loadRealTimeStats();
      generateKeyLearnings();

      // Only set up live updates if there are active/upcoming fights
      if (!areAllFightsCompleted()) {
        const interval = setInterval(() => {
          loadRealTimeStats();
          generateKeyLearnings();
        }, 10000); // Every 10 seconds for truly live data
        return () => clearInterval(interval);
      }
    }
  }, [event?.id, dataStore.isLoading, dataStore.votes, dataStore.fights, loadAnalytics, loadRealTimeStats, generateKeyLearnings, areAllFightsCompleted]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadAnalytics();
    loadRealTimeStats();
    setRefreshing(false);

    toast({
      title: 'Analytics refreshed',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleGenerateGraphic = async (graphicType) => {
    try {
      setGeneratingGraphic(true);

      let canvas;
      let filename;
      const timestamp = new Date().toISOString().split('T')[0];
      const eventSlug = event.name.replace(/[^a-zA-Z0-9]/g, '_');

      switch (graphicType) {
        case 'summary':
          canvas = await generateEventSummaryCard(event, analytics, realTimeStats);
          filename = `${eventSlug}_summary_${timestamp}.png`;
          break;

        case 'fanFavorites':
          const fanFavorites = getFanFavorites(false);
          if (fanFavorites.length === 0) {
            throw new Error('No fan favorites data available');
          }
          canvas = await generateFanFavoritesCard(event, fanFavorites, dataStore.getFighter);
          filename = `${eventSlug}_fan_favorites_${timestamp}.png`;
          break;

        case 'timeline':
          if (analytics.timelineData.length === 0) {
            throw new Error('No timeline data available');
          }
          canvas = await generateVoteTimelineHeatmap(event, analytics.timelineData);
          filename = `${eventSlug}_timeline_${timestamp}.png`;
          break;

        case 'fight':
          // Generate graphic for the most popular fight
          const topFight = analytics.fightStats.reduce((max, fight) =>
            fight.totalVotes > (max?.totalVotes || 0) ? fight : max, null
          );
          if (!topFight) {
            throw new Error('No fight data available');
          }
          canvas = await generateFightHighlightCard(event, topFight, dataStore.getFighter);
          filename = `${eventSlug}_fight_highlight_${timestamp}.png`;
          break;

        case 'insight':
          if (!keyLearnings || keyLearnings.length === 0) {
            throw new Error('No insights available');
          }
          canvas = await generateKeyInsightCard(event, keyLearnings[0]);
          filename = `${eventSlug}_insight_${timestamp}.png`;
          break;

        default:
          throw new Error('Unknown graphic type');
      }

      // Convert canvas to data URL for preview
      const dataUrl = canvas.toDataURL('image/png');

      // Show preview modal
      setPreviewGraphic({
        dataUrl,
        filename,
        canvas
      });
      onOpen();

    } catch (err) {
      console.error('Graphic generation error:', err);
      toast({
        title: 'Failed to generate graphic',
        description: err.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setGeneratingGraphic(false);
    }
  };

  const handleDownloadGraphic = () => {
    if (previewGraphic) {
      downloadCanvas(previewGraphic.canvas, previewGraphic.filename);
      onClose();

      toast({
        title: 'Graphic downloaded!',
        description: `${previewGraphic.filename} has been saved`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDownloadAll = async () => {
    try {
      setGeneratingGraphic(true);
      const timestamp = new Date().toISOString().split('T')[0];
      const eventSlug = event.name.replace(/[^a-zA-Z0-9]/g, '_');
      let downloadCount = 0;

      // Generate and download Event Summary
      const summaryCanvas = await generateEventSummaryCard(event, analytics, realTimeStats);
      downloadCanvas(summaryCanvas, `${eventSlug}_summary_${timestamp}.png`);
      downloadCount++;

      // Small delay between downloads to prevent browser blocking
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generate and download Fan Favorites (if available)
      const fanFavorites = getFanFavorites(false);
      if (fanFavorites.length > 0) {
        const favoritesCanvas = await generateFanFavoritesCard(event, fanFavorites, dataStore.getFighter);
        downloadCanvas(favoritesCanvas, `${eventSlug}_fan_favorites_${timestamp}.png`);
        downloadCount++;
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Generate and download Vote Timeline (if available)
      if (analytics.timelineData && analytics.timelineData.length > 0) {
        const timelineCanvas = await generateVoteTimelineHeatmap(event, analytics.timelineData);
        downloadCanvas(timelineCanvas, `${eventSlug}_timeline_${timestamp}.png`);
        downloadCount++;
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Generate and download Fight Highlight (if available)
      if (analytics.fightStats && analytics.fightStats.length > 0) {
        const topFight = analytics.fightStats.reduce((max, fight) =>
          fight.totalVotes > (max?.totalVotes || 0) ? fight : max, null
        );
        if (topFight) {
          const fightCanvas = await generateFightHighlightCard(event, topFight, dataStore.getFighter);
          downloadCanvas(fightCanvas, `${eventSlug}_fight_highlight_${timestamp}.png`);
          downloadCount++;
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Generate and download Key Insight (if available)
      if (keyLearnings && keyLearnings.length > 0) {
        const insightCanvas = await generateKeyInsightCard(event, keyLearnings[0]);
        downloadCanvas(insightCanvas, `${eventSlug}_insight_${timestamp}.png`);
        downloadCount++;
      }

      toast({
        title: 'All graphics downloaded!',
        description: `${downloadCount} graphics have been saved`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Download all error:', err);
      toast({
        title: 'Failed to download all graphics',
        description: err.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setGeneratingGraphic(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const data = dataStore.getEventAnalytics(event.id);
      let exportContent;

      if (format === 'csv') {
        // Create comprehensive CSV export
        const csvRows = [];

        // Event Summary Header
        csvRows.push('EVENT ANALYTICS SUMMARY');
        csvRows.push(`Event Name,${event.name}`);
        csvRows.push(`Generated At,${new Date().toISOString()}`);
        csvRows.push(`Total Votes,${data.voteStats.totalVotes}`);
        csvRows.push(`Total Participants,${data.userEngagement.totalUsers}`);
        csvRows.push(`Average Votes per User,${data.userEngagement.averageVotesPerUser.toFixed(2)}`);
        csvRows.push(`Overall Prediction Accuracy,${data.voteStats.averagePredictionAccuracy ? data.voteStats.averagePredictionAccuracy.toFixed(2) + '%' : 'N/A'}`);
        csvRows.push('');

        // Fight Details Header
        csvRows.push('FIGHT ANALYTICS');
        csvRows.push('Fight Name,Status,Total Votes,Vote Share %,Engagement Rate %,Prediction Accuracy %,Most Voted Fighter,Vote Split %,Winner');

        // Fight data rows
        data.fightStats.forEach(fight => {
          const voteEntries = Object.entries(fight.voteDistribution);
          const mostVoted = voteEntries.reduce(
            (max, [fighterId, votes]) => votes > (max?.votes || 0) ? { fighterId, votes } : max,
            null
          );

          const voteSplit = voteEntries.length > 1 ?
            Math.min(...voteEntries.map(([, votes]) => votes)) / Math.max(...voteEntries.map(([, votes]) => votes)) * 100 : 0;

          const engagementRate = data.userEngagement.totalUsers > 0 ?
            (fight.totalVotes / data.userEngagement.totalUsers) * 100 : 0;

          const voteShare = (fight.totalVotes / data.voteStats.totalVotes) * 100;

          csvRows.push([
            `"${fight.fightName}"`,
            fight.status,
            fight.totalVotes,
            voteShare.toFixed(1),
            engagementRate.toFixed(1),
            fight.predictionAccuracy ? fight.predictionAccuracy.toFixed(1) : 'N/A',
            mostVoted ? (() => {
              const fighter = dataStore.getFighter(mostVoted.fighterId);
              return fighter ? fighter.name : `Fighter ${mostVoted.fighterId.slice(-4)}`;
            })() : 'N/A',
            voteSplit.toFixed(1),
            fight.winnerId ? (() => {
              const winner = dataStore.getFighter(fight.winnerId);
              return winner ? winner.name : `Fighter ${fight.winnerId.slice(-4)}`;
            })() : 'TBD'
          ].join(','));
        });

        csvRows.push('');

        // Vote Timeline
        csvRows.push('VOTING TIMELINE');
        csvRows.push('Hour,Votes,Percentage of Total');
        const totalTimelineVotes = data.timelineData.reduce((sum, slot) => sum + slot.votes, 0);
        data.timelineData.forEach(timeSlot => {
          const percentage = totalTimelineVotes > 0 ? (timeSlot.votes / totalTimelineVotes) * 100 : 0;
          csvRows.push(`${formatHour(timeSlot.hour)},${timeSlot.votes},${percentage.toFixed(1)}%`);
        });

        csvRows.push('');

        // Fan Favorites
        const fanFavorites = getFanFavorites();
        if (fanFavorites.length > 0) {
          csvRows.push('FAN FAVORITES');
          csvRows.push('Rank,Fighter Name,Weight Class,Total Votes,Fights in Event');
          fanFavorites.forEach((item, index) => {
            csvRows.push([
              index + 1,
              `"${item.fighter.name}"`,
              item.fighter.weightClass || 'Unknown',
              item.votes,
              item.fightsInEvent
            ].join(','));
          });
          csvRows.push('');
        }

        // Key Learnings
        if (keyLearnings && keyLearnings.length > 0) {
          csvRows.push('KEY INSIGHTS');
          csvRows.push('Type,Title,Insight');
          keyLearnings.forEach(learning => {
            csvRows.push([
              learning.type,
              `"${learning.title}"`,
              `"${learning.insight}"`
            ].join(','));
          });
        }

        exportContent = csvRows.join('\n');
      } else {
        // Enhanced JSON export with additional metadata
        exportContent = JSON.stringify({
          eventInfo: {
            id: event.id,
            name: event.name,
            description: event.description,
            date: event.date,
            exportedAt: new Date().toISOString()
          },
          analytics: data,
          keyLearnings: keyLearnings || [],
          fanFavorites: getFanFavorites(),
          metadata: {
            totalFights: data.fightStats.length,
            completedFights: data.fightStats.filter(f => f.status === 'completed').length,
            activeFights: data.fightStats.filter(f => f.status === 'active').length,
            upcomingFights: data.fightStats.filter(f => f.status === 'upcoming').length
          }
        }, null, 2);
      }

      // Create download
      const blob = new Blob([exportContent], {
        type: format === 'csv' ? 'text/csv' : 'application/json'
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.name.replace(/[^a-zA-Z0-9]/g, '_')}-analytics-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: `Analytics exported as ${format.toUpperCase()}`,
        description: `Comprehensive ${format === 'csv' ? 'spreadsheet' : 'data'} export completed`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Export failed',
        description: err.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading analytics..." />;
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Box textAlign="center" py={10}>
        <Text color="gray.400">No analytics data available</Text>
      </Box>
    );
  }

  return (
    <Box maxW="7xl" mx="auto" p={6}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Event Analytics</Heading>
          <Text color="gray.400">{event.name}</Text>
          {realTimeStats && realTimeStats.generatedAt && (
            <Text fontSize="sm" color="gray.500">
              Last updated: {realTimeStats.generatedAt.toLocaleString('en-US', {
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                timeZoneName: 'short'
              })}
            </Text>
          )}
        </VStack>

        <HStack spacing={3}>
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={handleRefresh}
            isLoading={refreshing}
            size="sm"
            variant="outline"
          >
            Refresh
          </Button>
          <Button
            leftIcon={<FiDownload />}
            onClick={() => handleExport('json')}
            size="sm"
            colorScheme="blue"
          >
            Export JSON
          </Button>
          <Button
            leftIcon={<FiDownload />}
            onClick={() => handleExport('csv')}
            size="sm"
            variant="outline"
          >
            Export CSV
          </Button>
        </HStack>
      </Flex>

      {/* Marketing Graphics Download Section */}
      <Card mb={6} bg="purple.900" borderColor="purple.700">
        <CardHeader pb={2}>
          <HStack justify="space-between">
            <HStack>
              <Icon as={FiImage} color="purple.300" />
              <Heading size="md" color="purple.100">Marketing Graphics</Heading>
            </HStack>
            <Badge colorScheme="purple" fontSize="xs">PNG Downloads</Badge>
          </HStack>
        </CardHeader>
        <CardBody pt={2}>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text color="purple.200" mb={2} fontSize="sm" fontWeight="medium">
                Generate shareable PNG graphics from your event analytics data
              </Text>
              <List spacing={1} fontSize="xs" color="purple.300">
                <ListItem>
                  <ListIcon as={FiCheck} color="purple.400" />
                  Optimized for social media (Facebook, Instagram, Twitter)
                </ListItem>
                <ListItem>
                  <ListIcon as={FiCheck} color="purple.400" />
                  Professional quality, data-driven visuals
                </ListItem>
                <ListItem>
                  <ListIcon as={FiCheck} color="purple.400" />
                  Generated instantly from live analytics
                </ListItem>
              </List>
            </Box>

            <Tooltip label="Download all available graphics at once (Event Summary, Fan Favorites, Timeline, Fight Highlight, Key Insight)" placement="top">
              <Button
                leftIcon={<FiDownload />}
                colorScheme="green"
                variant="solid"
                onClick={handleDownloadAll}
                isLoading={generatingGraphic}
                size="sm"
                mb={3}
              >
                Download All Graphics
              </Button>
            </Tooltip>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={3}>
              <Tooltip label="1200x630px - Perfect for Facebook & Twitter posts. Click to preview before downloading." placement="top">
                <Button
                  size="sm"
                  leftIcon={<FiEye />}
                  colorScheme="purple"
                  variant="solid"
                  onClick={() => handleGenerateGraphic('summary')}
                  isLoading={generatingGraphic}
                  width="100%"
                >
                  Event Summary
                </Button>
              </Tooltip>

              <Tooltip label="1080x1080px - Instagram square. Top 3 most voted fighters with photos. Click to preview." placement="top">
                <Button
                  size="sm"
                  leftIcon={<FiEye />}
                  colorScheme="yellow"
                  variant="solid"
                  onClick={() => handleGenerateGraphic('fanFavorites')}
                  isLoading={generatingGraphic}
                  isDisabled={getFanFavorites(false).length === 0}
                  width="100%"
                >
                  Fan Favorites
                </Button>
              </Tooltip>

              <Tooltip label="1200x675px - 16:9 widescreen. Hour-by-hour voting activity heatmap. Click to preview." placement="top">
                <Button
                  size="sm"
                  leftIcon={<FiEye />}
                  colorScheme="blue"
                  variant="solid"
                  onClick={() => handleGenerateGraphic('timeline')}
                  isLoading={generatingGraphic}
                  isDisabled={!analytics?.timelineData || analytics.timelineData.length === 0}
                  width="100%"
                >
                  Vote Timeline
                </Button>
              </Tooltip>

              <Tooltip label="1200x630px - Showcase the most popular fight with vote percentages. Click to preview." placement="top">
                <Button
                  size="sm"
                  leftIcon={<FiEye />}
                  colorScheme="orange"
                  variant="solid"
                  onClick={() => handleGenerateGraphic('fight')}
                  isLoading={generatingGraphic}
                  isDisabled={!analytics?.fightStats || analytics.fightStats.length === 0}
                  width="100%"
                >
                  Fight Highlight
                </Button>
              </Tooltip>

              <Tooltip label="1080x1080px - Instagram story/square. AI-generated insight. Click to preview." placement="top">
                <Button
                  size="sm"
                  leftIcon={<FiEye />}
                  colorScheme="teal"
                  variant="solid"
                  onClick={() => handleGenerateGraphic('insight')}
                  isLoading={generatingGraphic}
                  isDisabled={!keyLearnings || keyLearnings.length === 0}
                  width="100%"
                >
                  Key Insight
                </Button>
              </Tooltip>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      {/* Real-time Stats */}
      <Card mb={6} bg="blue.900" borderColor="blue.700">
        <CardHeader pb={2}>
          <HStack justify="space-between">
            <HStack>
              <Icon as={FiActivity} color="blue.300" />
              <Heading size="md" color="blue.100">Live Statistics</Heading>
            </HStack>
            <HStack spacing={2}>
              {(() => {
                if (areAllFightsCompleted()) {
                  return (
                    <>
                      <Box w={2} h={2} bg="gray.400" borderRadius="full" />
                      <Text fontSize="xs" color="blue.200">
                        Event completed
                      </Text>
                    </>
                  );
                } else {
                  return (
                    <>
                      <Box w={2} h={2} bg="green.400" borderRadius="full" />
                      <Text fontSize="xs" color="blue.200">
                        Updates every 10s
                      </Text>
                    </>
                  );
                }
              })()}
            </HStack>
          </HStack>
        </CardHeader>
        <CardBody pt={2}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Stat>
              <StatLabel color="blue.200">Recent Activity</StatLabel>
              <StatNumber color="white">
                {realTimeStats?.recentVotes || 0}
              </StatNumber>
              <StatHelpText color="blue.300">
                votes in last 5 min
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel color="blue.200">Active Users</StatLabel>
              <StatNumber color="white">
                {realTimeStats?.activeUsers || 0}
              </StatNumber>
              <StatHelpText color="blue.300">
                active in last hour
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel color="blue.200">Voting Rate</StatLabel>
              <StatNumber color="white">
                {realTimeStats?.votingRate || '0.0'}
              </StatNumber>
              <StatHelpText color="blue.300">
                votes per minute
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel color="blue.200">Hourly Activity</StatLabel>
              <StatNumber color="white">
                {realTimeStats?.hourlyVotes || 0}
              </StatNumber>
              <StatHelpText color="blue.300">
                votes in last hour
              </StatHelpText>
            </Stat>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Top Voters Section */}
      {(() => {
        const topVoters = getTopVoters();
        if (topVoters.length === 0) return null;

        return (
          <Card mb={6} bg="purple.900" borderColor="purple.700">
            <CardHeader pb={2}>
              <HStack justify="space-between">
                <HStack>
                  <Icon as={FiAward} color="purple.300" />
                  <Heading size="md" color="purple.100">Top Voters</Heading>
                </HStack>
                <Badge colorScheme="purple" fontSize="xs">Most Correct</Badge>
              </HStack>
            </CardHeader>
            <CardBody pt={2}>
              <VStack spacing={3} align="stretch">
                {topVoters.map((voter) => {
                  const isTop3 = voter.rank <= 3;
                  const medal = voter.rank === 1 ? '🥇' : voter.rank === 2 ? '🥈' : voter.rank === 3 ? '🥉' : '';
                  const bgColor = voter.rank === 1 ? 'yellow.500' : voter.rank === 2 ? 'gray.400' : voter.rank === 3 ? 'orange.600' : 'purple.600';

                  return (
                    <Box
                      key={voter.userId}
                      p={4}
                      bg={isTop3 ? 'purple.800' : 'purple.850'}
                      borderRadius="md"
                      borderWidth={isTop3 ? '2px' : '1px'}
                      borderColor={isTop3 ? 'purple.500' : 'purple.700'}
                    >
                      <HStack justify="space-between">
                        <HStack spacing={3}>
                          <Box
                            minW="32px"
                            h="32px"
                            bg={bgColor}
                            borderRadius="full"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="lg" fontWeight="bold" color="white">
                              {medal || voter.rank}
                            </Text>
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold" color="white" fontSize="lg">
                              {voter.displayName}
                            </Text>
                            <Text fontSize="sm" color="purple.200">
                              {voter.email}
                            </Text>
                          </VStack>
                        </HStack>
                        <VStack align="end" spacing={0}>
                          <Text fontSize="2xl" fontWeight="bold" color="white">
                            {voter.correctVotes}
                          </Text>
                          <Text fontSize="sm" color="purple.200">
                            correct
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
              <Text fontSize="xs" color="purple.300" mt={3} textAlign="center">
                {(() => {
                  const rank3Count = topVoters.filter(v => v.rank === 3).length;
                  if (rank3Count > 1) {
                    return `Showing top 3 ranks with ${rank3Count} tied for 3rd place`;
                  }
                  return `Top ${topVoters.length} ${topVoters.length === 1 ? 'voter' : 'voters'} by correct predictions`;
                })()}
              </Text>
            </CardBody>
          </Card>
        );
      })()}

      {/* Key Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <HStack mb={2}>
                <Icon as={FiBarChart2} color="green.400" />
                <StatLabel color="white">Total Votes</StatLabel>
              </HStack>
              <StatNumber color="white">{analytics.voteStats.totalVotes}</StatNumber>
              <StatHelpText color="gray.300">
                {analytics.fightStats.length} fights • {analytics.voteStats.fightWinnerVotes} fight winner votes
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack mb={2}>
                <Icon as={FiUsers} color="blue.400" />
                <StatLabel color="white">Participants</StatLabel>
              </HStack>
              <StatNumber color="white">{analytics.userEngagement.totalUsers}</StatNumber>
              <StatHelpText color="gray.300">
                Avg {analytics.userEngagement.averageVotesPerUser.toFixed(1)} votes each
                {analytics.userEngagement.averageVotesPerUser > 5 && (
                  <StatArrow type="increase" />
                )}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack mb={2}>
                <Icon as={FiTarget} color="purple.400" />
                <StatLabel color="white">Prediction Accuracy</StatLabel>
              </HStack>
              <StatNumber color="white">
                {analytics.voteStats.averagePredictionAccuracy
                  ? `${analytics.voteStats.averagePredictionAccuracy.toFixed(1)}%`
                  : 'N/A'
                }
              </StatNumber>
              <StatHelpText color="gray.300">
                {analytics.fightStats.filter(f => f.status === 'completed').length} completed fights
                {analytics.voteStats.averagePredictionAccuracy > 60 && (
                  <StatArrow type="increase" />
                )}
                {analytics.voteStats.averagePredictionAccuracy < 40 && (
                  <StatArrow type="decrease" />
                )}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack mb={2}>
                <Icon as={FiClock} color="orange.400" />
                <StatLabel color="white">Peak Voting</StatLabel>
              </HStack>
              <StatNumber color="white">
                {analytics.timelineData.length > 0
                  ? formatHour(analytics.timelineData.reduce((max, curr) => curr.votes > max.votes ? curr : max).hour)
                  : 'N/A'
                }
              </StatNumber>
              <StatHelpText color="gray.300">
                {analytics.timelineData.length > 0
                  ? analytics.timelineData.reduce((max, curr) => curr.votes > max.votes ? curr : max).votes
                  : 0
                } votes
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Enhanced Event Statistics */}
      <Card mb={6}>
        <CardHeader>
          <Heading size="md" color="white">Event Performance Overview</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box>
              <HStack mb={2}>
                <Icon as={FiPercent} color="green.400" />
                <Text fontWeight="bold" color="white">Engagement Rate</Text>
              </HStack>
              <Text fontSize="2xl" fontWeight="bold" color="white">
                {analytics.userEngagement.totalUsers > 0
                  ? Math.round((analytics.voteStats.totalVotes / (analytics.userEngagement.totalUsers * analytics.fightStats.length)) * 100)
                  : 0
                }%
              </Text>
              <Text fontSize="sm" color="gray.300" mb={2}>
                Users voted on {analytics.userEngagement.totalUsers > 0
                  ? Math.round((analytics.voteStats.totalVotes / analytics.userEngagement.totalUsers / analytics.fightStats.length) * 100)
                  : 0
                }% of available fights
              </Text>
              <Text fontSize="xs" color="gray.400" fontStyle="italic">
                What this means: How many fights users voted on compared to total fights available. Higher = more engagement across the card.
              </Text>
            </Box>

            <Box>
              <HStack mb={2}>
                <Icon as={FiAward} color="yellow.400" />
                <Text fontWeight="bold" color="white">Top Performer</Text>
              </HStack>
              <Text fontSize="lg" fontWeight="bold" color="white">
                {(() => {
                  const topFight = analytics.fightStats.reduce((max, fight) =>
                    fight.totalVotes > (max?.totalVotes || 0) ? fight : max, null
                  );
                  return topFight ? topFight.fightName : 'N/A';
                })()}
              </Text>
              <Text fontSize="sm" color="gray.300" mb={2}>
                Most voted fight ({analytics.fightStats.reduce((max, fight) =>
                  Math.max(max, fight.totalVotes), 0
                )} votes)
              </Text>
              <Text fontSize="xs" color="gray.400" fontStyle="italic">
                What this means: The fight that generated the most fan interest and participation. This matchup resonated most with users.
              </Text>
            </Box>

            <Box>
              <HStack mb={2}>
                <Icon as={FiCheckCircle} color="green.400" />
                <Text fontWeight="bold" color="white">Prediction Accuracy</Text>
              </HStack>
              <Text fontSize="lg" fontWeight="bold" color="white">
                {analytics.fightStats.filter(f => f.status === 'completed').length > 0 && analytics.voteStats.averagePredictionAccuracy
                  ? `${Math.round(analytics.voteStats.averagePredictionAccuracy)}%`
                  : 'N/A'}
              </Text>
              <Text fontSize="sm" color="gray.300" mb={2}>
                {analytics.fightStats.filter(f => f.status === 'completed').length > 0
                  ? `Based on ${analytics.fightStats.filter(f => f.status === 'completed').length} completed fight${analytics.fightStats.filter(f => f.status === 'completed').length === 1 ? '' : 's'}`
                  : 'No completed fights yet'}
              </Text>
              <Text fontSize="xs" color="gray.400" fontStyle="italic">
                What this means: How often users correctly predicted fight outcomes. Higher = users know their fighters well.
              </Text>
            </Box>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* User Engagement Breakdown */}
      <Card mb={6}>
        <CardHeader>
          <Heading size="md" color="white">User Engagement Levels</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Box>
              <Text fontWeight="bold" mb={2} color="white">Total Users</Text>
              <Progress
                value={100}
                colorScheme="blue"
                mb={2}
              />
              <Text fontSize="sm" color="gray.300">
                {analytics.userEngagement.totalUsers} users participated
              </Text>
            </Box>

            <Box>
              <Text fontWeight="bold" mb={2} color="white">Average Votes per User</Text>
              <Progress
                value={Math.min((analytics.userEngagement.averageVotesPerUser / 10) * 100, 100)}
                colorScheme="green"
                mb={2}
              />
              <Text fontSize="sm" color="gray.300">
                {analytics.userEngagement.averageVotesPerUser.toFixed(1)} votes per user
              </Text>
            </Box>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Enhanced Fight Performance */}
      <Card mb={6}>
        <CardHeader>
          <Heading size="md" color="white">Detailed Fight Analytics</Heading>
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th color="gray.300">Fight</Th>
                  <Th color="gray.300">Status</Th>
                  <Th isNumeric color="gray.300">Votes</Th>
                  <Th isNumeric color="gray.300">Engagement</Th>
                  <Th isNumeric color="gray.300">Accuracy</Th>
                  <Th color="gray.300">Crowd Favorite</Th>
                  <Th color="gray.300">Vote Split</Th>
                </Tr>
              </Thead>
              <Tbody>
                {analytics.fightStats.map((fight) => {
                  const voteEntries = Object.entries(fight.voteDistribution);
                  const mostVoted = voteEntries.reduce(
                    (max, [fighterId, votes]) => votes > (max?.votes || 0) ? { fighterId, votes } : max,
                    null
                  );

                  // Calculate vote split (how evenly distributed the votes are)
                  const voteSplit = voteEntries.length > 1 ?
                    Math.min(...voteEntries.map(([, votes]) => votes)) / Math.max(...voteEntries.map(([, votes]) => votes)) * 100 : 0;

                  const engagementRate = analytics.userEngagement.totalUsers > 0 ?
                    (fight.totalVotes / analytics.userEngagement.totalUsers) * 100 : 0;

                  return (
                    <Tr key={fight.fightId}>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" fontWeight="medium" color="white">
                            {fight.fightName}
                          </Text>
                          {fight.status === 'completed' && fight.winnerId && (
                            <Text fontSize="xs" color="gray.300">
                              Winner: {(() => {
                                const winner = dataStore.getFighter(fight.winnerId);
                                return winner ? winner.name : `Fighter ${fight.winnerId.slice(-4)}`;
                              })()}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={
                            fight.status === 'completed' ? 'green' :
                              fight.status === 'active' ? 'blue' : 'gray'
                          }
                          size="sm"
                        >
                          {fight.status}
                        </Badge>
                      </Td>
                      <Td isNumeric color="gray.200">
                        <VStack spacing={0}>
                          <Text fontWeight="bold" color="white">{fight.totalVotes}</Text>
                          <Text fontSize="xs" color="gray.300">
                            {((fight.totalVotes / analytics.voteStats.totalVotes) * 100).toFixed(1)}%
                          </Text>
                        </VStack>
                      </Td>
                      <Td isNumeric color="gray.200">
                        <VStack spacing={0}>
                          <Text fontWeight="bold" color="white">{engagementRate.toFixed(1)}%</Text>
                          <Progress
                            value={Math.min(engagementRate, 100)}
                            size="xs"
                            colorScheme={engagementRate > 70 ? 'green' : engagementRate > 40 ? 'yellow' : 'red'}
                            w="40px"
                          />
                        </VStack>
                      </Td>
                      <Td isNumeric color="gray.200">
                        {fight.predictionAccuracy !== null ? (
                          <VStack spacing={0}>
                            <Text
                              fontWeight="bold"
                              color={fight.predictionAccuracy > 60 ? 'green.300' : fight.predictionAccuracy < 40 ? 'red.300' : 'yellow.300'}
                            >
                              {fight.predictionAccuracy.toFixed(1)}%
                            </Text>
                            {fight.predictionAccuracy > 80 && <Badge colorScheme="green" size="xs">Predictable</Badge>}
                            {fight.predictionAccuracy < 30 && <Badge colorScheme="red" size="xs">Upset</Badge>}
                          </VStack>
                        ) : (
                          <Text color="gray.400">-</Text>
                        )}
                      </Td>
                      <Td>
                        {mostVoted && (() => {
                          const fighter = dataStore.getFighter(mostVoted.fighterId);
                          const fighterName = fighter ? fighter.name : `Fighter ${mostVoted.fighterId.slice(-4)}`;
                          const isWinner = fight.status === 'completed' && fight.winnerId === mostVoted.fighterId;

                          return (
                            <VStack align="start" spacing={1}>
                              <HStack spacing={2}>
                                <Tooltip label={`${mostVoted.votes} votes (${((mostVoted.votes / fight.totalVotes) * 100).toFixed(1)}%)`}>
                                  <Text fontSize="sm" color="white" fontWeight="medium">
                                    {fighterName}
                                  </Text>
                                </Tooltip>
                                {isWinner && (
                                  <Badge colorScheme="green" size="sm" fontSize="xs">
                                    Winner ✓
                                  </Badge>
                                )}
                              </HStack>
                              <Progress
                                value={(mostVoted.votes / fight.totalVotes) * 100}
                                size="xs"
                                colorScheme="blue"
                                w="50px"
                              />
                            </VStack>
                          );
                        })()}
                      </Td>
                      <Td>
                        <VStack spacing={0}>
                          <Text fontSize="sm" color={voteSplit > 70 ? 'yellow.300' : voteSplit > 40 ? 'orange.300' : 'red.300'} fontWeight="bold">
                            {voteSplit.toFixed(0)}%
                          </Text>
                          <Text fontSize="xs" color="gray.200">
                            {voteSplit > 70 ? 'Close' : voteSplit > 40 ? 'Moderate' : 'Decisive'}
                          </Text>
                        </VStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      {/* Key Learnings Section */}
      {keyLearnings && keyLearnings.length > 0 && (
        <Card mb={6}>
          <CardHeader>
            <HStack>
              <Icon as={FiStar} color="yellow.400" />
              <Heading size="md" color="white">Key Insights & Learnings</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {keyLearnings.map((learning, index) => (
                <Box
                  key={index}
                  p={4}
                  bg={learning.type === 'success' ? 'green.900' : learning.type === 'warning' ? 'orange.900' : 'blue.900'}
                  borderLeft="4px solid"
                  borderLeftColor={learning.type === 'success' ? 'green.400' : learning.type === 'warning' ? 'orange.400' : 'blue.400'}
                  borderRadius="md"
                >
                  <HStack mb={2}>
                    <Icon as={learning.icon} color={learning.type === 'success' ? 'green.400' : learning.type === 'warning' ? 'orange.400' : 'blue.400'} />
                    <Text fontWeight="bold" color="white">{learning.title}</Text>
                  </HStack>
                  <Text color="gray.200" fontSize="sm">
                    {learning.insight}
                  </Text>
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Fan Favorites Section */}
      {(() => {
        const fanFavorites = getFanFavorites(showAllFighters);
        const allFighters = getFanFavorites(true);
        if (fanFavorites.length === 0) return null;

        return (
          <Card mb={6}>
            <CardHeader>
              <HStack>
                <Icon as={FiStar} color="yellow.400" />
                <Heading size="md" color="white">Fan Favorites</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {fanFavorites.map((item, index) => {
                  const maxVotes = fanFavorites[0]?.votes || 1;
                  const percentage = (item.votes / maxVotes) * 100;

                  return (
                    <Box key={item.fighterId} p={4} bg="dark.700" borderRadius="md">
                      <HStack justify="space-between" mb={2}>
                        <HStack spacing={3}>
                          <Box
                            minW="24px"
                            h="24px"
                            bg={index === 0 ? 'yellow.500' : index === 1 ? 'gray.400' : index === 2 ? 'orange.600' : 'blue.600'}
                            borderRadius="full"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="sm" fontWeight="bold" color="white">
                              {index + 1}
                            </Text>
                          </Box>
                          {/* Fighter profile picture */}
                          {(item.fighter.photoURL || item.fighter.photoUrl) && (
                            <Box
                              w="60px"
                              h="60px"
                              borderRadius="full"
                              overflow="hidden"
                              border="2px solid"
                              borderColor={index === 0 ? 'yellow.500' : index === 1 ? 'gray.400' : index === 2 ? 'orange.600' : 'blue.600'}
                              flexShrink={0}
                            >
                              <Image
                                src={item.fighter.photoURL || item.fighter.photoUrl}
                                alt={item.fighter.name}
                                w="100%"
                                h="100%"
                                objectFit="cover"
                              />
                            </Box>
                          )}
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold" color="white" fontSize="lg">
                              {item.fighter.name}
                            </Text>
                            <HStack spacing={4}>
                              <Text fontSize="sm" color="gray.300">
                                {item.fighter.weightClass || 'Unknown Weight Class'}
                              </Text>
                              <Text fontSize="sm" color="gray.400">
                                {item.fightsInEvent} fight{item.fightsInEvent !== 1 ? 's' : ''} in event
                              </Text>
                            </HStack>
                          </VStack>
                        </HStack>
                        <VStack align="end" spacing={0}>
                          <Text fontSize="xl" fontWeight="bold" color="white">
                            {item.votes}
                          </Text>
                          <Text fontSize="sm" color="gray.300">
                            votes
                          </Text>
                        </VStack>
                      </HStack>

                      <Box>
                        <HStack justify="space-between" mb={1}>
                          <Text fontSize="sm" color="gray.400">
                            Fan Support
                          </Text>
                          <Text fontSize="sm" color="gray.400">
                            {((item.votes / fanFavorites[0].votes) * 100).toFixed(0)}% of top vote count
                          </Text>
                        </HStack>
                        <Progress
                          value={percentage}
                          colorScheme={index === 0 ? 'yellow' : index === 1 ? 'gray' : index === 2 ? 'orange' : 'blue'}
                          size="sm"
                          borderRadius="md"
                        />
                      </Box>
                    </Box>
                  );
                })}
              </VStack>

              <VStack spacing={3} mt={4}>
                <Text fontSize="sm" color="gray.400" textAlign="center">
                  {showAllFighters
                    ? `Showing all ${fanFavorites.length} fighters with votes`
                    : fanFavorites.length === 10
                      ? `Showing top 10 of ${allFighters.length} fighters`
                      : `Showing all ${fanFavorites.length} fighters with votes`
                  }
                </Text>

                {(allFighters.length > 10 && !showAllFighters) && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                    onClick={() => setShowAllFighters(!showAllFighters)}
                  >
                    Show All
                  </Button>
                )}
              </VStack>
            </CardBody>
          </Card>
        );
      })()}

      {/* Vote Timeline */}
      {analytics.timelineData.length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="md" color="white">Voting Activity by Hour</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4}>
              {analytics.timelineData.map((timeSlot) => {
                const maxVotes = Math.max(...analytics.timelineData.map(t => t.votes));
                const intensity = (timeSlot.votes / maxVotes) * 100;
                return (
                  <Box key={timeSlot.hour} p={3} bg="dark.700" borderRadius="md" textAlign="center" position="relative">
                    <Box
                      position="absolute"
                      bottom={0}
                      left={0}
                      right={0}
                      height={`${intensity}%`}
                      bg="blue.500"
                      opacity={0.3}
                      borderRadius="md"
                    />
                    <Text fontWeight="bold" color="white" position="relative" zIndex={1}>
                      {formatHour(timeSlot.hour)}
                    </Text>
                    <Text fontSize="sm" color="gray.300" position="relative" zIndex={1}>
                      {timeSlot.votes} votes
                    </Text>
                    {timeSlot.votes === maxVotes && (
                      <Badge colorScheme="blue" size="sm" mt={1}>Peak</Badge>
                    )}
                  </Box>
                );
              })}
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Graphic Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="dark.800" maxW="90vw">
          <ModalHeader color="white">
            <VStack align="start" spacing={1}>
              <Text>Graphic Preview</Text>
              <Text fontSize="sm" color="gray.400" fontWeight="normal">
                {previewGraphic?.filename}
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              {previewGraphic && (
                <Box
                  maxW="100%"
                  maxH="70vh"
                  overflow="auto"
                  bg="dark.900"
                  p={4}
                  borderRadius="md"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Image
                    src={previewGraphic.dataUrl}
                    alt="Graphic preview"
                    maxW="100%"
                    maxH="65vh"
                    objectFit="contain"
                    borderRadius="md"
                  />
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onClose} color="white">
                Cancel
              </Button>
              <Button
                leftIcon={<FiDownload />}
                colorScheme="blue"
                onClick={handleDownloadGraphic}
              >
                Download PNG
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default EventAnalytics;
